import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
  applyRoasTableToDocumentXml,
  cleanupRepaymentCellInlineTableText,
  ROAS_TABLE_PLACEHOLDER,
} from './buildRoasCovenantTableXml';
import {
  buildExpandedAmountClause,
  fmtUsdDocx,
  pctDisplayNumber,
} from './termSheetFormatters';
import {
  buildCohortCapLine,
  buildMaxCacClauseDocx,
  buildMinInvestmentLineDocx,
  buildMonthlyCohortLineDocx,
  buildPctOfFundingClause,
} from './buildInvestmentSizeSection';
import { buildFundingFeeClauseDocx } from './buildFundingFeeSection';
import {
  applyOptionalSectionsToDocumentXml,
  buildNonUtilizationFeeClause,
  buildWarrantsClause,
} from './buildOptionalSections';
import {
  buildRepaymentProportionalLine,
  buildRepaymentSetoffLine,
  buildRepaymentCohortFootnoteDocx,
  buildSecurityClause,
} from './buildRepaymentSection';
import { buildConfidentialityClause } from './buildConfidentialitySection';
import { buildCappedReturnClause, buildCappedReturnIrrLine, buildCappedReturnTotalFeeLine } from './buildCappedReturnSection';
import { buildNoShopClause } from './buildNoShopSection';
import { buildCompanyClause } from './buildCompanySection';
import { buildCostsAndExpensesClause } from './buildCostsAndExpensesSection';
import {
  computeTotalCohortInvestment,
} from './termSheetPayload';
import { buildRoasCurveClause } from './buildRoasTablePreview';
import { sanitizeTermSheetDocxZip } from './sanitizeTermSheetDocxZip';
import { normalizeTermSheetDocxZip } from './normalizeTermSheetDocumentXml';

const TEMPLATE_URL = `${process.env.PUBLIC_URL || ''}/term-sheet-template.docx?v=20260823o`;

function invisibleBulletPlaceholder(value) {
  const text = String(value ?? '').trim();
  return text || '\u00A0';
}

function fmtUsd(value) {
  return fmtUsdDocx(value);
}

function fmtNum(value) {
  if (value === '' || value == null) return '[]';
  const n = Number(value);
  if (!Number.isFinite(n)) return '[]';
  return String(n);
}

function fmtPctPlain(value) {
  const pct = pctDisplayNumber(value);
  if (pct == null) return '[]';
  return pct;
}

function buildTokenMap(fields) {
  const total = computeTotalCohortInvestment(fields);
  const roasCurveClause = buildRoasCurveClause(fields);

  return {
    TS_COMPANY_CLAUSE: buildCompanyClause(fields),
    TS_MAX_OUTSTANDING: fmtUsd(fields.maxOutstandingInvestment),
    TS_EXPANDED_AMOUNT_CLAUSE: buildExpandedAmountClause(fields.expandedAmount),
    TS_MONTHLY_COHORT_LINE: buildMonthlyCohortLineDocx(
      fields,
      fmtUsd,
      fmtNum,
      total
    ),
    TS_PCT_OF_FUNDING_LINE: buildPctOfFundingClause(fields),
    TS_COHORT_CAP_LINE: invisibleBulletPlaceholder(
      buildCohortCapLine(fields, fmtPctPlain, fmtUsd)
    ),
    TS_MIN_INVESTMENT_LINE: invisibleBulletPlaceholder(
      buildMinInvestmentLineDocx(fields, fmtUsd)
    ),
    TS_MAX_CAC_CLAUSE: buildMaxCacClauseDocx(fields, fmtUsd),
    TS_TERM_MONTHS: fmtNum(fields.termMonths),
    TS_TERM_COHORTS: fmtNum(fields.termCohorts),
    TS_REPAYMENT_PROPORTIONAL_LINE: buildRepaymentProportionalLine(),
    TS_ROAS_CURVE_CLAUSE: roasCurveClause,
    TS_REPAYMENT_SETOFF_LINE: buildRepaymentSetoffLine(),
    TS_REPAYMENT_COHORT_FOOTNOTE: buildRepaymentCohortFootnoteDocx(fields),
    TS_SECURITY_CLAUSE: buildSecurityClause(fields),
    TS_CAPPED_RETURN_IRR_LINE: buildCappedReturnIrrLine(fields),
    TS_CAPPED_RETURN_TOTAL_FEE_LINE: buildCappedReturnTotalFeeLine(fields),
    TS_CONFIDENTIALITY_CLAUSE: buildConfidentialityClause(fields),
    TS_NO_SHOP_CLAUSE: buildNoShopClause(fields),
    TS_COSTS_AND_EXPENSES_CLAUSE: buildCostsAndExpensesClause(fields),
    TS_FUNDING_FEE_CLAUSE: buildFundingFeeClauseDocx(fields, fmtUsd, fmtPctPlain),
    TS_MIN_LIQ_MONTHS: fmtNum(fields.minLiquidityMonths),
    TS_MIN_LIQ_USD: fmtUsd(fields.minLiquidityUsd),
    TS_NON_UTILIZATION_CLAUSE: buildNonUtilizationFeeClause(fields),
    TS_WARRANTS_CLAUSE: buildWarrantsClause(fields),
  };
}

function cleanupRoasCurveOptionText(xml) {
  return xml
    .replace(
      /\s*\[Note to Draft: Include second option if the amounts in the table will be filled out, otherwise include first option\]/gi,
      ''
    )
    .replace(
      /\[as indicatively set out in the table below\]\s*\[OR\]\s*\[as indicatively set out in the table below\]/g,
      'as indicatively set out in the table below'
    )
    .replace(/\[to be agreed\]\s*\[OR\]\s*\[to be agreed\]/g, 'to be agreed')
    .replace(
      /Minimum ROAS curve \[\[TS_ROAS_CURVE_OPTION\]\] between the parties, must be maintained, with a breach triggering accelerated repayment of tested cohorts\./g,
      '[[TS_ROAS_CURVE_CLAUSE]]'
    )
    .replace(
      /\[to be agreed\]\s*\[OR\]\s*\[as indicatively set out in the table below\]/g,
      '[[TS_ROAS_CURVE_CLAUSE]]'
    );
}

export function stripRoasDraftNotes(xml) {
  return cleanupRoasCurveOptionText(xml);
}

export async function buildTermSheetDocxBlob(fields) {
  const response = await fetch(TEMPLATE_URL);
  if (!response.ok) {
    throw new Error('Failed to load term sheet template');
  }
  const templateBuffer = await response.arrayBuffer();
  const zip = await JSZip.loadAsync(templateBuffer);
  const documentXml = await zip.file('word/document.xml')?.async('string');
  if (!documentXml) {
    throw new Error('Invalid term sheet template');
  }

  let xml = cleanupRepaymentCellInlineTableText(documentXml);
  xml = cleanupRoasCurveOptionText(xml);
  const tokens = buildTokenMap(fields);
  for (const [token, value] of Object.entries(tokens)) {
    xml = xml.split(`[[${token}]]`).join(escapeXml(value));
  }
  xml = cleanupRoasCurveOptionText(xml);
  xml = applyRoasTableToDocumentXml(xml, fields);
  xml = applyOptionalSectionsToDocumentXml(xml, fields);

  await sanitizeTermSheetDocxZip(zip);
  zip.file('word/document.xml', xml, { createFolders: false });
  await normalizeTermSheetDocxZip(zip);
  return zip.generateAsync({
    type: 'blob',
    mimeType:
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    compression: 'DEFLATE',
  });
}

export async function buildTermSheetDocxArrayBuffer(fields) {
  const blob = await buildTermSheetDocxBlob(fields);
  return blob.arrayBuffer();
}

function escapeXml(value) {
  return String(value ?? '')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export async function downloadTermSheetDocx({ fields, filenameBase = 'term-sheet' }) {
  const blob = await buildTermSheetDocxBlob(fields);
  const safeBase = (filenameBase || 'term-sheet').replace(/[^\w.-]+/g, '-');
  saveAs(blob, `${safeBase}.docx`);
}

export function buildTermSheetDocxTokensForTest(fields) {
  return buildTokenMap(fields);
}
